import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  useTheme,
  useMediaQuery,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Paper,
  Grid,
} from "@mui/material";
import { Button } from "../../atoms";
import {
  useGetMerchantWithVkn,
  useGetMerchantWithTckn,
  useAddMerchantWithTodebInfos,
  useSearchMobilEftPos,
  useSearchFixedEftPos,
} from "../../../hooks/todeb";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { useNavigate } from "react-router-dom";

const initialInput = "";

function MerchantTodeb() {
  const [input, setInput] = useState(initialInput);
  const [open, setOpen] = useState(false);
  const [searchType, setSearchType] = useState<"vkn" | "tckn" | null>(null);
  const [triggerValue, setTriggerValue] = useState<string>("");

  const [posInput, setPosInput] = useState("");
  const [posModalOpen, setPosModalOpen] = useState(false);
  const [posType, setPosType] = useState<"mobil" | "sabit">("mobil");
  const [posTriggerValue, setPosTriggerValue] = useState<string>("");

  const setSnackbar = useSetSnackBar();
  const navigate = useNavigate();

  const handleSearch = () => {
    if (input.length === 10) {
      setSearchType("vkn");
      setTriggerValue(input);
      setOpen(true);
    } else if (input.length === 11) {
      setSearchType("tckn");
      setTriggerValue(input);
      setOpen(true);
    }
  };

  const handlePosSearch = () => {
    if (posInput.length === 10) {
      setPosTriggerValue(posInput);
      setPosModalOpen(true);
    }
  };

  const { data: vknData, isLoading: vknLoading } = useGetMerchantWithVkn(
    searchType === "vkn" ? triggerValue : undefined
  );
  const { data: tcknData, isLoading: tcknLoading } = useGetMerchantWithTckn(
    searchType === "tckn" ? triggerValue : undefined
  );
  const { mutate: addMerchantWithTodebInfos, isLoading: addLoading } =
    useAddMerchantWithTodebInfos();

  const { data: mobilPosData, isLoading: mobilPosLoading } =
    useSearchMobilEftPos(
      posType === "mobil" && posTriggerValue ? posTriggerValue : undefined
    );
  const { data: fixedPosData, isLoading: fixedPosLoading } =
    useSearchFixedEftPos(
      posType === "sabit" && posTriggerValue ? posTriggerValue : undefined
    );

  const responseData = searchType === "vkn" ? vknData : tcknData;
  const model = responseData?.data;
  const loading = vknLoading || tcknLoading || addLoading;

  const posResponseData = posType === "mobil" ? mobilPosData : fixedPosData;
  const posModel = posResponseData?.data;
  const posLoading = mobilPosLoading || fixedPosLoading;

  const handleAddMerchant = () => {
    if (model) {
      addMerchantWithTodebInfos(model, {
        onSuccess: (data) => {
          if (data.isSuccess) {
            setSnackbar({
              severity: "success",
              isOpen: true,
              description: "İşyeri başarıyla eklendi",
            });
            setOpen(false);

            localStorage.removeItem("merchantData");
            navigate("/merchant-management/merchant-identification", {
              state: data.data,
            });
          } else {
            setSnackbar({
              severity: "error",
              description: data.message || "İşlem sırasında bir hata oluştu",
              isOpen: true,
            });
          }
        },
        onError: () => {
          setSnackbar({
            severity: "error",
            description: "İşlem sırasında bir hata oluştu",
            isOpen: true,
          });
        },
      });
    }
  };

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <>
      {/* Main Search Section */}
      <Box
        p={3}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Stack
          direction={isDesktop ? "row" : "column"}
          spacing={3}
          divider={
            isDesktop ? <Divider orientation="vertical" flexItem /> : undefined
          }
          sx={{ width: "100%", maxWidth: 1000 }}
        >
          {/* Left Card: VKN / TCKN Search */}
          <Paper elevation={4} sx={{ p: 4, flex: 1, borderRadius: 2, alignContent: 'center' }}>
            <Stack spacing={3} alignItems={'center'}>
              <Box sx={{ fontWeight: "bold", fontSize: "1.1rem", mb: 1 }}>
                VKN / TCKN Sorgulama
              </Box>
              <Stack
                direction={'column'}
                spacing={2}
                alignItems="center"
                width={'100%'}
              >
                <TextField
                  label="VKN / TCKN"
                  value={input}
                  onChange={(e) =>
                    setInput(e.target.value.replace(/[^0-9]/g, "").slice(0, 11))
                  }
                  variant="outlined"
                  fullWidth
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 11 }}
                />
                <Button
                  text="Ara"
                  variant="contained"
                  size="medium"
                  disabled={!(input.length === 10 || input.length === 11)}
                  onClick={handleSearch}
                />
              </Stack>
            </Stack>
          </Paper>

          {/* Right Card: POS Search */}
          <Paper elevation={4} sx={{ p: 4, flex: 1, borderRadius: 2, alignContent: 'center' }}>
            <Stack spacing={3} alignItems='center'>
              <Box sx={{ fontWeight: "bold", fontSize: "1.1rem", mb: 1 }}>
                POS Sorgulama
              </Box>
              <Stack
                direction={"column"}
                spacing={2}
                alignItems="center"
                width={'100%'}
              >
                <TextField
                  label="VKN (POS Sorgusu)"
                  value={posInput}
                  onChange={(e) =>
                    setPosInput(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))
                  }
                  variant="outlined"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 10 }}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>POS Tipi</InputLabel>
                  <Select
                    value={posType}
                    fullWidth
                    label="POS Tipi"
                    onChange={(e) =>
                      setPosType(e.target.value as "mobil" | "sabit")
                    }
                  >
                    <MenuItem value="mobil">Mobil POS</MenuItem>
                    <MenuItem value="sabit">Sabit POS</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  text="POS Sorgula"
                  variant="contained"
                  size="medium"
                  disabled={posInput.length !== 10}
                  onClick={handlePosSearch}
                />
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Box>


      {/* Merchant Info Modal */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "grey.100", py: 2 }}>
          Tödeb Bilgileri
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {loading ? (
            <Box textAlign="center">Yükleniyor...</Box>
          ) : model ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ bgcolor: "grey.50", p: 1, borderRadius: 1, mt: 2 }}>
                <strong>Genel Bilgiler</strong>
              </Box>
              <TextField
                label="Ticaret Ünvanı"
                value={model.tradeName || ""}
                fullWidth
                disabled
              />
              <TextField
                label="Ad"
                value={model.name || ""}
                fullWidth
                disabled
              />
              <TextField
                label="Vergi Numarası"
                value={model.taxNumber || ""}
                fullWidth
                disabled
              />
              <TextField
                label="TC Kimlik No"
                value={model.tcIdentityNumber || ""}
                fullWidth
                disabled
              />
              <TextField
                label="Vergi Dairesi"
                value={model.taxOfficeName || ""}
                fullWidth
                disabled
              />
              <TextField
                label="Vergi Dairesi Kodu"
                value={model.taxOfficeCode || ""}
                fullWidth
                disabled
              />
              <TextField
                label="Kuruluş Tarihi"
                value={model.establishmentDate || ""}
                fullWidth
                disabled
              />

              {/* İkametgah Adresi */}
              {model.addresses?.find((addr: any) => addr.addressType === "1") && (
                <>
                  <Box
                    sx={{ bgcolor: "grey.50", p: 1, borderRadius: 1, mt: 2 }}
                  >
                    <strong>İkametgah Adresi Detayları</strong>
                  </Box>
                  {["cityCode", "districtCode", "district", "street", "frontDoorNumber", "doorNumber", "address"].map(
                    (field) => (
                      <TextField
                        key={field}
                        label={`İkametgah - ${field}`}
                        value={
                          model.addresses?.find(
                            (addr: any) => addr.addressType === "1"
                          )?.[field] || ""
                        }
                        fullWidth
                        disabled
                        multiline={field === "address"}
                        rows={field === "address" ? 2 : 1}
                      />
                    )
                  )}
                </>
              )}

              {/* İşyeri Adresi */}
              {model.addresses?.find((addr: any) => addr.addressType === "2") && (
                <>
                  <Box
                    sx={{ bgcolor: "grey.50", p: 1, borderRadius: 1, mt: 2 }}
                  >
                    <strong>İşyeri Adresi Detayları</strong>
                  </Box>
                  {["cityCode", "districtCode", "district", "street", "frontDoorNumber", "doorNumber", "address"].map(
                    (field) => (
                      <TextField
                        key={field}
                        label={`İşyeri - ${field}`}
                        value={
                          model.addresses?.find(
                            (addr: any) => addr.addressType === "2"
                          )?.[field] || ""
                        }
                        fullWidth
                        disabled
                        multiline={field === "address"}
                        rows={field === "address" ? 2 : 1}
                      />
                    )
                  )}
                </>
              )}
            </Stack>
          ) : (
            <Box textAlign="center" color="error.main">
              Kayıt bulunamadı.
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button text="Kapat" onClick={() => setOpen(false)} variant="outlined" />
          {model && (
            <Button
              text="İşyeri Olarak Ekle"
              onClick={handleAddMerchant}
              variant="contained"
              disabled={loading}
            />
          )}
        </DialogActions>
      </Dialog>

      {/* POS Modal */}
      <Dialog
        open={posModalOpen}
        onClose={() => setPosModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "grey.100", py: 2 }}>
          {posType === "mobil" ? "Mobil" : "Sabit"} POS Bilgileri
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {posLoading ? (
            <Box textAlign="center">Yükleniyor...</Box>
          ) : posModel ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Sonuç"
                value={JSON.stringify(posModel, null, 2)}
                fullWidth
                disabled
                multiline
                rows={10}
              />
            </Stack>
          ) : (
            <Box textAlign="center" color="error.main">
              POS kaydı bulunamadı.
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            text="Kapat"
            onClick={() => setPosModalOpen(false)}
            variant="outlined"
          />
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MerchantTodeb;
